import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ExpandMoreIcon } from 'applicaiton/assets/expand-more.svg';
import useOnClickOutside from 'common/hooks/useClickOutside';
import Input from '../Input';
import Checkbox from '../Checkbox';
import { DropdownProps, Option } from './models';
import {
  DropdownStyled,
  OptionCheckBox,
  OptionItem,
  OptionsList,
} from './styles';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useDebounce } from '../../hooks/useDebounce';

/**
 * A component that renders a dropdown with options for selection.
 * The dropdown can be single or multi-select, and can also have search functionality.
 *
 * @param id - A unique identifier for the dropdown
 * @param label - An optional label for the dropdown
 * @param placeholder - The placeholder text to display when no value is selected
 * @param searchPlaceholder - The placeholder text to display in the search input
 * @param value - The currently selected value(s) of the dropdown
 * @param onChange - A callback function that is called when the selected value(s) change
 * @param hint - An optional hint message to display below the dropdown
 * @param disabled - Whether the dropdown should be disabled or not
 * @param options - The list of options to display in the dropdown
 * @param isMulti - Whether the dropdown allows multiple selections
 * @param errorMessage - An optional error message to display below the dropdown if validation fails
 * @param withSearch - Whether the dropdown should include a search input
 * @param LeftIcon - An optional icon to display on the left side of the dropdown
 * @param getNewData - A function that is called when the user scrolls to the bottom of the options list and there are more pages to load
 * @param hasNextPage - Whether there are more pages of options to load
 * @param onSearchData - A function that is called when the search input value changes, which should update the list of options displayed in the dropdown
 * @param currentValue - The current value of the dropdown
 */
const InfinityScrollDropdown: FC<DropdownProps> = ({
  id,
  label,
  placeholder,
  searchPlaceholder,
  value,
  onChange,
  hint,
  disabled,
  options,
  isMulti,
  errorMessage,
  withSearch,
  LeftIcon,
  getNewData,
  hasNextPage,
  onSearchData,
  currentValue,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);

  const [isActive, setIsActive] = useState(false);
  const [searchItem, setSearchItem] = useState('');

  const searchByName = useDebounce(searchItem, 500);

  useOnClickOutside(ref, () => setIsActive(false));

  useEffect(() => {
    onSearchData(searchByName);
  }, [searchByName]);

  const [sentryRef] = useInfiniteScroll({
    loading: false,
    hasNextPage,
    onLoadMore: () => {
      getNewData();
    },
    disabled: false,
    rootMargin: '0px 0px 400px 0px',
  });

  const handleChange = (selectedItem: string) => {
    if (!Array.isArray(value)) return;
    const valueAlreadyAdded = value.some((item) => item === selectedItem);
    if (valueAlreadyAdded) {
      const filtredValue = value.filter((item) => item !== selectedItem);
      onChange(filtredValue);
    } else {
      onChange([...value, selectedItem]);
    }
    return;
  };

  /**
   * A method that checks whether a given option is currently selected.
   *
   * @param item - The option to check
   */
  const IsValueChecked = (item: Option) => {
    // checking if checkbox checked for dropdown multiple
    const isValueArray = Array.isArray(value);
    return isValueArray && value.some((i) => i === item.value);
  };

  /**
   * A method that returns the currently selected value(s) in the dropdown.
   */
  const getValue = () => {
    if (isMulti) return `${value.length} ${t('selected')}`;
    const currentOption = options.find(
      (item) => String(item.value) === String(value),
    );
    return currentOption ? currentOption?.label : currentValue;
  };

  return (
    <DropdownStyled
      isActive={isActive}
      disabled={disabled}
      ref={ref}
      isError={!!errorMessage}
    >
      {label && <label htmlFor={id}>{label}</label>}
      <section onMouseDown={() => !disabled && setIsActive(!isActive)}>
        {LeftIcon ? (
          <div>
            <LeftIcon />
            {!value?.length ? (
              <span>{placeholder}</span>
            ) : (
              <h2>{getValue()}</h2>
            )}
          </div>
        ) : (
          <>
            {!value?.length ? (
              <span>{placeholder}</span>
            ) : (
              <h2>{getValue()}</h2>
            )}
          </>
        )}
        <ExpandMoreIcon />
      </section>
      {isActive && !isMulti ? (
        <OptionsList className="optionList">
          {withSearch && (
            <Input
              id={'search'}
              type="search"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target?.value || '')}
              placeholder={searchPlaceholder}
              onClear={() => setSearchItem('')}
            />
          )}
          {!!options?.length &&
            options?.map((item) => (
              <OptionItem
                key={item.value}
                onClick={() => {
                  onChange(item);
                  setIsActive(false);
                }}
                selected={value === String(item.value)}
              >
                {item.label}
              </OptionItem>
            ))}
          <div ref={sentryRef}></div>
        </OptionsList>
      ) : (
        ''
      )}
      {isActive && isMulti ? (
        <OptionsList className="optionListMulti">
          {withSearch && (
            <Input
              id={'search'}
              type="search"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target?.value || '')}
              placeholder={searchPlaceholder}
              onClear={() => setSearchItem('')}
            />
          )}
          {options.map((item, index) => (
            <OptionCheckBox
              key={`${item.value}_${index}`}
              htmlFor={`${item.label}_${index}`}
              selected={IsValueChecked(item)}
            >
              <Checkbox
                id={`${item.label}_${index}`}
                checked={IsValueChecked(item)}
                onChange={() => handleChange(String(item.value))}
              />
              {item.label}
            </OptionCheckBox>
          ))}
          <div ref={sentryRef}></div>
        </OptionsList>
      ) : (
        ''
      )}
      {(hint || errorMessage) && (
        <span>{errorMessage ? errorMessage : hint}</span>
      )}
    </DropdownStyled>
  );
};

export default InfinityScrollDropdown;
